import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, intlFormatDistance } from 'date-fns';
import { Typography, Box, Card, MuiBox, MuiTable, Grid, SvgIcon, Tooltip } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kiosksSelectors, actions as kiosksActions, Kiosk } from 'store/kiosks';
import { formatTitle, capitalizeFirst } from 'utils/stringFormat';
import KioskActions from './KioskActions';
import KioskHardware from './KioskStatus';
import Popup from './Popup';
import { useStyles } from '../styled';
import { KioskTableProps } from '../types';

export const sort = (
  data: Kiosk[],
  stockCountsByPrinter: Map<string, Record<string, number>>,
  sortValue: string,
  sortDirection: string
): Kiosk[] => {
  const mapElement = stockCountsByPrinter.get(sortValue);

  return data.sort((a, b) => {
    let sortA: string | number = '';
    let sortB: string | number = '';

    // Determine if the sortValue is a Kiosk property, otherwise it is
    // a printer displayName and we need to sort by stock count
    if (['name', 'venueName', 'lastPrint', 'status'].includes(sortValue)) {
      // Sort by a Kiosk property
      const value: keyof Kiosk = sortValue as keyof Kiosk;
      sortA = (a[value] as string) || '';
      sortB = (b[value] as string) || '';

      // check to avoid case insensitive data and to handle date fields
      if (typeof sortA === 'string' && typeof sortB === 'string') {
        if (value === 'lastPrint') {
          sortA = sortA ? new Date(sortA).getTime() : 0;
          sortB = sortB ? new Date(sortB).getTime() : 0;
        } else if (value === 'status') {
          sortA = a.online ? sortA.toLowerCase() : 'offline';
          sortB = b.online ? sortB.toLowerCase() : 'offline';
        } else {
          sortA = sortA.toLowerCase();
          sortB = sortB.toLowerCase();
        }
      }
    } else {
      // Sort by the stock count of a printer (displayName)
      sortA = (mapElement && mapElement[a.id]) || 0;
      sortB = (mapElement && mapElement[b.id]) || 0;
    }

    if (sortA < sortB) {
      return sortDirection === 'asc' ? -1 : 1;
    } else if (sortA > sortB) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

const KioskTable = (props: KioskTableProps) => {
  const { pending, sortParams, setSortParams, message, onEdit, onDelete, showHiddenKiosks } = props;
  const { column: sortColumn, order: sortOrder } = sortParams;

  // ACTIONS AND SELECTORS
  const { getKiosks, resetMessage } = useActions({
    getKiosks: kiosksActions.getKiosks,
    resetMessage: kiosksActions.resetMessage
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kiosks = useSelector(kiosksSelectors.selectKiosks);
  const autoRefresh = useSelector(kiosksSelectors.selectAutoRefresh);
  const stockCountsByPrinter = useSelector(kiosksSelectors.selectStockCountsByPrinter);

  const clientRef = useRef(client.id);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const lastKioskAPIRef = useRef(0);

  const [tabVisible, setTabVisible] = useState(true);
  const [sortedKiosks, setSortedKiosks] = useState<Kiosk[]>([]);

  const filteredKiosks = showHiddenKiosks ? kiosks : kiosks.filter((k) => k.configuration?.showInKCC);

  const classes = useStyles()();

  // Calculate counts for Active, Inactive, and Offline of real kiosks.
  const counts = React.useMemo(() => {
    const c = { total: filteredKiosks.length, active: 0, inactive: 0, offline: 0 };
    for (const k of filteredKiosks) {
      if (!k.online) {
        c.offline += 1;
      } else if (k.status === 'Active') {
        c.active += 1;
      } else if (k.status === 'Inactive') {
        c.inactive += 1;
      }
    }
    return c;
  }, [filteredKiosks]);

  useEffect(() => {
    clientRef.current = '';
    timerRef.current = undefined;
    lastKioskAPIRef.current = 0;
    document.addEventListener('visibilitychange', handleVisibilityChange, true);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
    };
  }, []);

  useEffect(() => {
    if (!autoRefresh || clientRef.current !== client.id) {
      getKiosks({});
      clientRef.current = client.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // By default, data will be sorted based on name in ascending order
  useEffect(() => {
    if (kiosks) {
      const sortedData = sort(filteredKiosks, stockCountsByPrinter, sortColumn, sortOrder);
      setSortedKiosks(sortedData);
      lastKioskAPIRef.current = new Date().getTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosks, sortParams, showHiddenKiosks]);

  useEffect(() => {
    if (autoRefresh && tabVisible) {
      if (lastKioskAPIRef.current + 30000 < new Date().getTime()) getKiosks({});
      timerRef.current = setInterval(() => {
        getKiosks({});
      }, 30000);
    } else {
      clearInterval(timerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, tabVisible]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setTabVisible(false);
    } else {
      setTabVisible(true);
    }
  };

  const handleHeaderClick = (key: string) => {
    if (sortColumn === key) {
      setSortParams({ column: key, order: sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortParams({ column: key, order: 'asc' }); // For new column, sort in asc order.
    }
  };

  const SortArrow = (sortArrowProps: { type: string }) => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => handleHeaderClick(sortArrowProps.type)}>
        <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  return (
    <>
      {message && message.length > 0 && !pending && <Popup message={message} resetMessage={resetMessage} />}
      {/* TABLE OF KIOSKS */}
      <MuiBox className={classes.kioskDetailsOuterBox}>
        <Card hasBorderRadius raised fullHeight sx={{ flex: '1 0 0', display: 'flex', flexDirection: 'column' }}>
          <MuiTable.TableContainer id="kioskDetailsTable" sx={{ flex: 1, overflowY: 'auto' }}>
            <MuiTable.Table stickyHeader>
              <MuiTable.TableHead>
                <MuiTable.TableRow>
                  <MuiTable.TableCell
                    className={classes.kioskDetailsHeaders}
                    sx={{ width: '220px', paddingLeft: '24px', whiteSpace: 'nowrap' }}
                    onClick={() => handleHeaderClick('name')}
                  >
                    Kiosk
                    {sortColumn === 'name' && <SortArrow type={'name'} />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.kioskDetailsHeaders}
                    sx={{ width: '200px', whiteSpace: 'nowrap' }}
                    onClick={() => handleHeaderClick('venueName')}
                  >
                    Location
                    {sortColumn === 'venueName' && <SortArrow type={'venueName'} />}
                  </MuiTable.TableCell>
                  {stockCountsByPrinter.size > 0 &&
                    [...stockCountsByPrinter.keys()].map((key) => (
                      <MuiTable.TableCell
                        className={classes.kioskDetailsHeaders}
                        sx={{ width: '160px', whiteSpace: 'nowrap', textAlign: 'center' }}
                        onClick={() => handleHeaderClick(key)}
                      >
                        {key} Remaining
                        {sortColumn === key && <SortArrow type={key} />}
                      </MuiTable.TableCell>
                    ))}
                  <MuiTable.TableCell
                    className={classes.kioskDetailsHeaders}
                    sx={{ width: '160px', whiteSpace: 'nowrap' }}
                    onClick={() => handleHeaderClick('lastPrint')}
                  >
                    Last Delivery
                    {sortColumn === 'lastPrint' && <SortArrow type={'lastPrint'} />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.kioskDetailsHeaders}
                    onClick={() => handleHeaderClick('status')}
                    sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}
                  >
                    Status
                    {sortColumn === 'status' && <SortArrow type={'status'} />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.kioskDetailsHeaders}
                    sx={{ cursor: 'default', textAlign: 'center' }}
                  >
                    Actions
                  </MuiTable.TableCell>
                </MuiTable.TableRow>
              </MuiTable.TableHead>
              <MuiTable.TableBody>
                {sortedKiosks &&
                  sortedKiosks.map((kioskDetailsObj: Kiosk) => (
                    <MuiTable.TableRow
                      key={kioskDetailsObj.id}
                      className={
                        kioskDetailsObj.status === 'Active' && kioskDetailsObj.online
                          ? classes.Active
                          : classes.Inactive
                      }
                    >
                      <MuiTable.TableCell className={classes.kioskDetailsKioskName}>
                        <Typography fontSize={16}>{formatTitle(kioskDetailsObj.name)}</Typography>
                      </MuiTable.TableCell>
                      <MuiTable.TableCell className={classes.kioskDetailsCell}>
                        <Typography fontSize={16}>{kioskDetailsObj.venueName}</Typography>
                      </MuiTable.TableCell>
                      {stockCountsByPrinter.size > 0 &&
                        [...stockCountsByPrinter.values()].map((value) => (
                          <MuiTable.TableCell className={classes.kioskDetailsCell} sx={{ textAlign: 'center' }}>
                            <Typography fontSize={16}>{value[kioskDetailsObj.id] || '-'}</Typography>
                          </MuiTable.TableCell>
                        ))}
                      <MuiTable.TableCell className={classes.kioskDetailsCell}>
                        <Tooltip
                          title={
                            kioskDetailsObj.lastPrint
                              ? format(new Date(kioskDetailsObj.lastPrint), 'MM/dd/yyyy hh:mm:ss a')
                              : ''
                          }
                          arrow
                          placement={'top'}
                          verticalOffset={-8}
                        >
                          <Grid style={{ cursor: kioskDetailsObj.lastPrint ? 'pointer' : 'auto' }}>
                            <Typography fontSize={16}>
                              {kioskDetailsObj.lastPrint
                                ? capitalizeFirst(intlFormatDistance(new Date(kioskDetailsObj.lastPrint), new Date()))
                                : '-'}
                            </Typography>
                          </Grid>
                        </Tooltip>
                      </MuiTable.TableCell>
                      <MuiTable.TableCell>
                        <KioskHardware kiosk={kioskDetailsObj} />
                      </MuiTable.TableCell>
                      <MuiTable.TableCell>
                        <KioskActions
                          kiosk={kioskDetailsObj}
                          onEdit={() => onEdit(kioskDetailsObj)}
                          onDelete={() => onDelete(kioskDetailsObj)}
                        />
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>
          Kiosks: {counts.total} (Active: {counts.active}, Inactive: {counts.inactive}, Offline: {counts.offline})
        </p>
      </Grid>
    </>
  );
};

export default KioskTable;
